import { Component, OnDestroy, OnInit, Inject, Renderer2 } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { DOCUMENT } from '@angular/common';

import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { notifyMessage } from '../../../shared/constants/notify-message';
import { QualificationsService } from '../../services/qualification/qualifications.service';
import {
  AdvanceQuestion,
  AdvanceTargetingQualification,
  Category,
  RangeOption,
  SearchAdvanceQuestionsPayload,
} from '../../../shared/interfaces/qualification.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Constants } from '../../../shared/constants/create-survey.constants';

@Component({
  selector: 'ps-ui-advance-targeting-modal',
  templateUrl: './advance-targeting-modal.component.html',
  styleUrls: ['./advance-targeting-modal.component.css'],
})
export class AdvanceTargetingModalComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(ModalData)
    private _modalData: {
      question: AdvanceTargetingQualification | undefined;
      advanceTargetingQualifications: AdvanceTargetingQualification[];
      locale: string;
      isQlcLiveSurvey: boolean;
      alwaysShowQualCodes: number[];
    },
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private _fb: FormBuilder,
    private _toastr: ToasterService,
    private _qualificationsService: QualificationsService,
    private _modalRef: ModalRef<
      AdvanceTargetingModalComponent,
      {
        msg: string;
        question: AdvanceTargetingQualification;
        parent: AdvanceTargetingQualification | undefined;
      }
    >
  ) {}

  private _subscriptions: Subscription = new Subscription();
  form!: FormGroup;
  public questions: AdvanceQuestion[] = [];
  public question?: AdvanceTargetingQualification = this._modalData.question;
  private _parentQuestion!: AdvanceTargetingQualification;
  public locale: string = this._modalData.locale;
  public isQlcLiveSurvey: boolean = this._modalData.isQlcLiveSurvey;
  public rangeOptions: RangeOption[] = Constants.RANGE_OPTIONS;
  public showCategoryFilter: boolean = false;
  public categories: Category[] = [];
  public selectedCategories: number[] = [];

  ngOnInit(): void {
    this._getAdvancedQuestions();
    this._getCategories();
    this._initForm();
    if (this.question) {
      this._setSelectedQuestion(this.question);
    }
    this._renderer.addClass(this._document.body, 'modal-open');
  }

  private _initForm() {
    this.form = this._fb.group({
      qualification_name: [null, Validators.required],
      qualification_code: [null, Validators.required],
      question_type: [null],
      questionText: [null],
      answers: [null],
      selectedAnswers: [null],
      rangeOption: [null],
      rangeSet: this._fb.group({
        from: [null],
        to: [null],
        unit: [Constants.UNITS.AGE.YEAR],
      }),
      hasDependency: [false],
      dependency: [null],
    });
  }

  private _getCategories() {
    this._qualificationsService.getCategories().subscribe(
      (categories: Category[]) => {
        this.categories = categories;
      },
      (error) => {
        this._toastr.error(error.error.msg);
      }
    );
  }

  private _getAdvancedQuestions() {
    const payload = this._getAdvancedQuestionPayload();
    const getQuestions$ = this._qualificationsService
      .getQuestions(payload)
      .pipe(debounceTime(2000), distinctUntilChanged())
      .subscribe(
        (response) => {
          this.questions = this._filterAdvancedQuestions(response.questions);
          this._setQuestionDescription(this.questions);
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
    this._subscriptions.add(getQuestions$);
  }

  private _setQuestionDescription(questions: AdvanceQuestion[]) {
    const question = questions.find(
      (q) => q.qualification_id[0] === this.question?.qualification_code
    );

    if (question) {
      if (this.question) {
        this.question.question_type = question.question_type;
      }
      const { answers, ...updatedQuestion } = question; // here `answers` is an object instead of array so we are removing it from patchValue as it will be patched in the next step.
      this.form.patchValue(updatedQuestion);
      this._setAnswers(question);
      this.form.patchValue({
        questionText: this._getQuestionText(question),
      });
    }
  }

  private _getAdvancedQuestionPayload() {
    const payload: SearchAdvanceQuestionsPayload = {
      locale: this.locale,
      notIds: [],
      phrase: '',
      categories: this.selectedCategories,
    };
    return payload;
  }

  private _setAnswers(question: AdvanceQuestion) {
    if (
      this.question?.question_type !== 'range' &&
      !this.question?.answers?.length
    ) {
      this.form.patchValue({
        answers: this._mapAnswers(question),
      });
    } else {
      let index = 2;
      if (
        !this.question.rangeSet?.to ||
        this.question.rangeSet?.to === Constants.TO_MAX_RANGE_VALUE
      ) {
        index = 1;
      } else if (!this.question.rangeSet?.from) {
        index = 0;
      }
      this.form.patchValue({
        rangeOption: this.rangeOptions[index],
      });
    }
  }

  private _filterAdvancedQuestions(questions: AdvanceQuestion[]) {
    if (!this._modalData.advanceTargetingQualifications.length) {
      return this._removeDeprecatedQuestions(questions);
    }

    const existingQualifications =
      this._modalData.advanceTargetingQualifications
        .filter(
          (qualification) =>
            qualification.qualification_code !==
            this.question?.qualification_code
        )
        .map((qualification) => qualification.qualification_code);

    const filteredQuestions = questions.filter(
      (question) =>
        !existingQualifications.includes(question.qualification_id[0])
    );
    return this._removeDeprecatedQuestions(filteredQuestions);
  }

  private _setSelectedQuestion(question: AdvanceTargetingQualification) {
    this.form.patchValue(question);
    this.form.controls['qualification_name'].disable();
    if (question.childs?.length) {
      this.form.controls['selectedAnswers'].disable();
    }
  }

  selectQuestion(question?: AdvanceQuestion) {
    this.form.reset();

    if (!question) {
      return;
    }

    this.form.patchValue({
      qualification_name: question.question_description,
      qualification_code: question.qualification_id[0],
      question_type: question.question_type,
      questionText: this._getQuestionText(question),
      hasDependency: question.hasDependency,
    });

    if (question.hasDependency && question.dependency) {
      this.form.patchValue({ dependency: question.dependency });
      this._setParentQuestion(question);
    }

    if (question.question_type !== 'range') {
      this.form.patchValue({ answers: this._mapAnswers(question) });
    }
  }

  private _getQuestionText(questionData: AdvanceQuestion) {
    return questionData.buyer_text.replace(
      `%%${questionData.qualification_id[0]}%%`,
      ``
    );
  }

  private _setParentQuestion(childQuestion: AdvanceQuestion) {
    const parentQual = this.questions.find(
      (question) =>
        question.qualification_id[0] === childQuestion.dependency?.parentQualId
    );

    if (parentQual) {
      const answers = this._mapAnswers(parentQual);
      const selectedAnswers = this._getSelectedAnswers(childQuestion, answers);
      this._parentQuestion = {
        qualification_name: parentQual.question_description,
        question_type: parentQual.question_type,
        qualification_code: parentQual.qualification_id[0],
        questionText: this._getQuestionText(parentQual),
        answers: answers,
        selectedAnswers: selectedAnswers,
        hasDependency: parentQual.hasDependency,
        childs: [childQuestion.qualification_id[0]],
        flexible: { enable: true, value: 0 },
      };
    }
  }

  private _mapAnswers(question: AdvanceQuestion) {
    const qualId = question.qualification_id[0].toString();
    return (
      question.answers[qualId]?.answer_data.map((answer) => {
        return { id: answer.id, text: answer.text };
      }) || []
    );
  }

  private _getSelectedAnswers(
    childQuestion: AdvanceQuestion,
    answers: Array<{ id: number; text: string }>
  ) {
    if (childQuestion.dependency?.matchCriteria === 'in') {
      return answers.filter((answer) =>
        childQuestion.dependency?.condition.includes(answer.id)
      );
    }

    if (childQuestion.dependency?.matchCriteria === 'nin') {
      return answers.filter(
        (answer) => !childQuestion.dependency?.condition.includes(answer.id)
      );
    }

    return [];
  }

  applyTargeting() {
    if (this.form.valid && this._validAnswers()) {
      this.form.controls['qualification_name'].enable();
      this.form.controls['selectedAnswers'].enable();
      this._modalRef.close({
        msg: 'applyTargeting',
        question: this.form.value,
        parent: this._parentQuestion,
      });
    } else {
      this._toastr.warning(notifyMessage.warningMessage.INCOMPLETE_FIELDS);
    }
  }

  searchAdvanceQuestions(search: string, question: AdvanceQuestion) {
    search = search.toLocaleLowerCase();
    return (
      question.question_description.toLocaleLowerCase().indexOf(search) > -1 ||
      question.qualification_id.includes(Number(search))
    );
  }

  setRangeOption(value: RangeOption) {
    this.form.patchValue({
      rangeOption: value,
      rangeSet: { from: null, to: null },
    });
  }

  private _validAnswers() {
    if (this.form.value.question_type === 'range' && this._validRange()) {
      return true;
    }

    if (this.form.value.selectedAnswers?.length) {
      return true;
    }

    return false;
  }

  private _validRange() {
    if (!this.form.value.rangeOption) {
      return false;
    }

    if (
      this.form.value.rangeOption.id === 'gt' &&
      !this.form.value.rangeSet.from
    ) {
      return false;
    }

    if (
      this.form.value.rangeOption.id === 'lt' &&
      !this.form.value.rangeSet.to
    ) {
      return false;
    }

    if (
      this.form.value.rangeOption.id === 'bt' &&
      (!this.form.value.rangeSet.from ||
        !this.form.value.rangeSet.to ||
        this.form.value.rangeSet.from > this.form.value.rangeSet.to)
    ) {
      return false;
    }
    return true;
  }

  public onSelectAll() {
    const selected = this.form.value.answers;
    this.form.controls['selectedAnswers'].patchValue(selected);
  }

  public onClearAll() {
    this.form.controls['selectedAnswers'].patchValue([]);
  }

  private _removeDeprecatedQuestions(questions: AdvanceQuestion[]) {
    return questions?.filter((question) => {
      const showQual = this._modalData.alwaysShowQualCodes?.some((qualId) =>
        question.qualification_id.includes(Number(qualId))
      );
      return !question.deprecated || showQual;
    });
  }

  removeFilterCategories() {
    this.showCategoryFilter = !this.showCategoryFilter;
    if (!this.showCategoryFilter) {
      this.filterCategories([]);
    }
  }

  filterCategories(selectedCategories: Category[]) {
    this.selectedCategories = selectedCategories.map((category) => category.id);
    this._getAdvancedQuestions();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this._renderer.removeClass(this._document.body, 'modal-open');
  }
}
